import { useEffect, useRef } from "react";
import { gsap, prefersReducedMotion, revealWords, revealBlock } from "../lib/motion";
import { SERVICES, DOWNLOAD_URL } from "../data/site";
import Button from "./ui/Button";
import Icon3D from "./ui/Icon3D";
import handPhone from "../assets/images/hand-phone.webp";
import blobOrange from "../assets/decor/blob-orange.webp";
import waveSection from "../assets/decor/wave-section.webp";
import iconSecure from "../assets/icons/icon-secure.webp";
import iconReviews from "../assets/icons/icon-reviews.webp";
import iconSupport from "../assets/icons/icon-support.webp";
import "./Services.css";

const ICONS = { secure: iconSecure, reviews: iconReviews, support: iconSupport };

export default function Services() {
  const rootRef = useRef(null);
  const phoneRef = useRef(null);
  const waveRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const ctx = gsap.context(() => {
      revealWords(root.querySelector(".services__title"), { trigger: root });
      revealBlock(root.querySelector(".services__intro"), { delay: 0.2 });
      revealWords(root.querySelector(".band__pitch-title"), {
        trigger: root.querySelector(".band"),
      });

      if (prefersReducedMotion()) {
        gsap.set(".service, .band__cta, .band__phone, .services__blob", {
          autoAlpha: 1,
          x: 0,
          y: 0,
          scale: 1,
        });
        return;
      }

      gsap.fromTo(
        ".service",
        { autoAlpha: 0, x: -38 },
        {
          autoAlpha: 1,
          x: 0,
          duration: 1,
          ease: "expo.out",
          stagger: 0.13,
          scrollTrigger: { trigger: ".band", start: "top 72%", once: true },
        }
      );

      gsap.fromTo(
        phoneRef.current,
        { autoAlpha: 0, y: 70, scale: 0.95 },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 1.5,
          ease: "expo.out",
          scrollTrigger: { trigger: ".band", start: "top 78%", once: true },
        }
      );

      gsap.fromTo(
        ".band__cta",
        { autoAlpha: 0, y: 24 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.9,
          ease: "power3.out",
          delay: 0.3,
          scrollTrigger: { trigger: ".band", start: "top 72%", once: true },
        }
      );

      gsap.fromTo(
        ".services__blob",
        { autoAlpha: 0, scale: 0.3 },
        {
          autoAlpha: 1,
          scale: 1,
          duration: 1,
          ease: "back.out(2)",
          scrollTrigger: { trigger: root, start: "top 80%", once: true },
        }
      );

      // Depth inside the band.
      gsap.to(phoneRef.current, {
        yPercent: -7,
        ease: "none",
        scrollTrigger: { trigger: ".band", start: "top bottom", end: "bottom top", scrub: 0.7 },
      });

      gsap.to(waveRef.current, {
        xPercent: -6,
        ease: "none",
        scrollTrigger: { trigger: ".band", start: "top bottom", end: "bottom top", scrub: 1.1 },
      });
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section className="services section" id="services" ref={rootRef}>
      <div className="container">
        <div className="services__head">
          <div className="services__head-left">
            <img
              className="services__blob"
              src={blobOrange}
              alt=""
              aria-hidden="true"
              width="65"
              height="65"
            />
            <h2 className="services__title section-title">Your Jordanian Adventure Awaits</h2>
          </div>
          <p className="services__intro lede">
            Private Stays connects you with the best of Jordan&rsquo;s accommodations.
          </p>
        </div>

        <div className="band">
          <div
            className="band__wave"
            ref={waveRef}
            style={{ backgroundImage: `url(${waveSection})` }}
            aria-hidden="true"
          />

          <ul className="band__services">
            {SERVICES.map((s) => (
              <li className="service" key={s.id}>
                <Icon3D
                  className="service__icon"
                  name={s.icon}
                  fallbackSrc={ICONS[s.icon]}
                  bare
                />
                <h3 className="service__label">{s.title}</h3>
              </li>
            ))}
          </ul>

          <img
            ref={phoneRef}
            className="band__phone"
            src={handPhone}
            alt="The Private Stays app open on a phone held in one hand."
            width="800"
            height="665"
            loading="lazy"
            decoding="async"
          />

          <div className="band__pitch">
            <h3 className="band__pitch-title">
              Plenty Of Features To Make Your Booking Smother
            </h3>
            <div className="band__cta">
              <Button href={DOWNLOAD_URL} variant="dark" className="band__btn">
                Download now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
